/**
 * Working day arithmetic, section 4.4.
 *
 * The lab's weekend is Friday and Saturday. The weekend is a constant rather
 * than a literal in the loop, because it is the kind of thing that changes.
 *
 * The SERVER is authoritative for `deliveryDate` and `daysLeft`. These
 * functions exist for the live preview inside the intake form while the user is
 * choosing a start date, and for the tests that pin the rule.
 */

/** getDay() values. 5 is Friday, 6 is Saturday. */
export const WEEKEND_DAYS: readonly number[] = [5, 6];

/** Delivery date defaults to start date plus seven working days. */
export const DEFAULT_LEAD_WORKING_DAYS = 7;

export function isWeekend(date: Date): boolean {
  return WEEKEND_DAYS.includes(date.getDay());
}

export function isWorkingDay(date: Date): boolean {
  return !isWeekend(date);
}

function assertUsable(date: Date, label: string): void {
  if (Number.isNaN(date.getTime())) {
    throw new RangeError(`${label} is not a valid date`);
  }
}

/**
 * Adds n working days, skipping the weekend. Supports a negative n, which walks
 * backwards, and returns a copy for n of 0 without moving off a weekend day.
 * That last behaviour is deliberate and matches the prototype: adding nothing
 * to a Friday gives back that Friday, it does not silently become a Sunday.
 */
export function addWorkingDays(from: Date, n: number): Date {
  assertUsable(from, 'from');
  if (!Number.isInteger(n)) {
    throw new RangeError('n must be a whole number of working days');
  }

  const result = new Date(from.getTime());
  const step = n >= 0 ? 1 : -1;
  const target = Math.abs(n);

  let moved = 0;
  while (moved < target) {
    result.setDate(result.getDate() + step);
    if (isWorkingDay(result)) moved += 1;
  }
  return result;
}

function startOfDay(date: Date): Date {
  const copy = new Date(date.getTime());
  copy.setHours(0, 0, 0, 0);
  return copy;
}

/**
 * Working days from a to b. Negative when b is before a, so that
 * `workingDaysBetween(d, addWorkingDays(d, n))` is n for every n, which is the
 * property `daysLeft` depends on.
 *
 * The time of day is discarded: two instants on the same calendar day are zero
 * working days apart, whatever the clock says.
 */
export function workingDaysBetween(a: Date, b: Date): number {
  assertUsable(a, 'a');
  assertUsable(b, 'b');

  const from = startOfDay(a);
  const to = startOfDay(b);
  if (from.getTime() === to.getTime()) return 0;

  const step = to.getTime() > from.getTime() ? 1 : -1;
  const cursor = from;
  let count = 0;

  while (cursor.getTime() !== to.getTime()) {
    cursor.setDate(cursor.getDate() + step);
    /*
     * Back to midnight after every step, and not for tidiness.
     *
     * `setDate` keeps the time of day, and a zone whose clocks change AT
     * midnight -- Iran until 2022, Brazil, Chile, Lord Howe -- has no 00:00 on
     * the day of the change, so the cursor lands an hour off. From then on it
     * misses `to` by that hour on every comparison and this loop never ends.
     * The productivity report of 20 September 2026 walks from a task back to
     * its case's delivery date, which is far enough to reach such a day, and a
     * hung request is worse than a figure out by one.
     */
    cursor.setHours(0, 0, 0, 0);
    if (isWorkingDay(cursor)) count += step;
  }
  return count;
}

/** The delivery date the intake form previews while the user picks a start date. */
export function defaultDeliveryDate(startDate: Date): Date {
  return addWorkingDays(startDate, DEFAULT_LEAD_WORKING_DAYS);
}
